import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import { Alert, Container, Form } from "react-bootstrap";
import AdequacaoHistoricoCiclos from "../components/AdequacaoHistoricoCiclos/AdequacaoHistoricoCiclos";
import AccordionSection from "../components/AccordionSection/AccordionSection";
import AdimplenciaSection from "../components/AdimplenciaSection/AdimplenciaSection";
import CampoPergunta from "../components/CampoPergunta/CampoPergunta";
import CampoUpload from "../components/CampoUpload/CampoUpload";
import SaveSubmitButtons from "../../../../../components/SaveSubmitButtons/SaveSubmitButtons";
import NotificationToast from "../../../../../components/NotificationToast/NotificationToast";
import {
  patchAvaliacaoSolicitacao,
  uploadArquivoRespostaCampo,
  deleteArquivoRespostaCampo,
  downloadArquivoRespostaCampo,
  extrairArquivoRespostaCampoId,
} from "../../../../../services";
import type { RequestRespostaCampoJson } from "../../../../../services";
import type {
  AdimplenciaMeta,
  SecaoDocumento,
} from "../../../../../domain/shipping/models";
import { normalizarValorSimNaoParaUI } from "../../../../../domain/shipping/mappers";
import type {
  AssociadoAnaliseItem,
  ReceitaOperacionalAnaliseItem,
} from "../../../../../services";
import AnalistaIndicadoresSecao from "../components/AnalistaIndicadoresSecao/AnalistaIndicadoresSecao";
import type {
  AnaliseCamposPorSecao,
  AnaliseStatusUI,
  CiclosHistoricoAdequacaoPorAnaliseKey,
  HistoricoAdequacaoPorAnaliseKey,
} from "../../../Analysis/V2/buildAnaliseCamposPorSecaoId";
import { formatDateTimeBR } from "../../../../../utils/date";
import "../../V1/Tabs/DocObrigatoriosTab/DocObrigatoriosTab.css";

export type { SecaoDocumento };


function normalizarAnaliseDevolverSomenteAnalista(
  map: Record<string, AnaliseCamposPorSecao>,
  secoes: SecaoDocumento[],
): Record<string, AnaliseCamposPorSecao> {
  let changed = false;
  const next: Record<string, AnaliseCamposPorSecao> = { ...map };
  for (const secao of secoes) {
    if (!secao.somenteAnalistaVariant) continue;
    const cur = next[secao.id];
    if (cur?.status === "DEVOLVER") {
      next[secao.id] = { ...cur, status: "NAO_CONFORME" };
      changed = true;
    }
  }
  return changed ? next : map;
}

function tituloPareceCriterioDre(titulo: string | undefined): boolean {
  const t = String(titulo || "").toLowerCase();
  return (
    t.includes("dre") ||
    t.includes("demonstração") ||
    t.includes("demonstracao") ||
    t.includes("demonstrativo") ||
    (t.includes("resultado") &&
      (t.includes("exercício") ||
        t.includes("exercicio") ||
        t.includes("demonstr")))
  );
}


function formatarMoedaBrSemSimbolo(valor: number): string {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(valor);
}


function secaoTemApenasOpcoesSimNao(secao: {
  opcoes?: { valor: string; id?: number }[];
}): boolean {
  const opts = secao.opcoes ?? [];
  if (opts.length !== 2) return false;
  const a = normalizarValorSimNaoParaUI(opts[0]?.valor);
  const b = normalizarValorSimNaoParaUI(opts[1]?.valor);
  return (a === "SIM" && b === "NÃO") || (a === "NÃO" && b === "SIM");
}

export type TipoDocumentosTab = "obrigatorios" | "bonus" | "bonus-extra";

export interface DocumentosTabV2Props {
  isActive?: boolean;
  tipo: TipoDocumentosTab;
  tituloAba: string;
  descricaoGeral?: string;
  secoes: SecaoDocumento[];
  solicitacaoId?: number | null;
  onProximo?: () => void;
  onStatusChange?: (statusBySection: Record<string, boolean>) => void;
  onSaveAll?: () => Promise<
    { show?: boolean; message?: string; type?: string; reload?: boolean } | void
  >;
  submitLabel?: string;
  submitDisabled?: boolean;
  onEnviarPreConfirmacao?: () => Promise<
    { show?: boolean; message?: string; type?: string } | void
  >;
  
  readOnly?: boolean;
  
  parecerPorSecaoId?: Record<string, (string | React.ReactElement)[]>;
  
  dadosIndicadoresAnalise?: {
    receitasOperacionais: ReceitaOperacionalAnaliseItem[];
    associados: AssociadoAnaliseItem[];
    
    salarioMinimoAnoAnterior?: number | null;
  } | null;
  
  analiseCamposPorSecaoId?: Record<string, AnaliseCamposPorSecao>;
  
  analisePodeEditar?: boolean;
  
  onAnaliseCamposChange?: (
    value: Record<string, AnaliseCamposPorSecao>,
  ) => void;
  
  receitaOperacionalReferencia?: number | null;
  
  onReceitaOperacionalReferenciaChange?: (value: number) => void;
  
  associadosReferencia?: number | null;
  
  onAssociadosReferenciaChange?: (value: number) => void;
  
  analiseAguardandoRetornoPorSecaoId?: Record<string, boolean>;

  /** Quando true (ex.: análise V2), bloqueio global até o sindicato responder à adequação — mensagem distinta de “análise concluída”. */
  analiseBloqueadaPorAdequacaoGlobal?: boolean;
  
  historicoAdequacaoPorAnaliseKey?: HistoricoAdequacaoPorAnaliseKey;
  
  ciclosHistoricoAdequacaoPorAnaliseKey?: CiclosHistoricoAdequacaoPorAnaliseKey;
}

export interface DocumentosTabV2RefHandle {
  handleSave?: () => Promise<{ show?: boolean; message?: string; type?: string }>;
  isDirty?: () => boolean;
  getCamposAlterados?: () => RequestRespostaCampoJson[];
  getAnaliseCriterioSolicitacaoAlterados?: () => Array<{
    id: number;
    criterioId: number;
    observacaoSindicato?: string | null;
  }>;
  markAsSaved?: () => void;
  toggleAll?: () => void;
  hasPendingUploads?: () => boolean;
  getNomesArquivoLocais?: () => Record<string, string>;
}

const DocumentosTabV2 = forwardRef<DocumentosTabV2RefHandle, DocumentosTabV2Props>(
  (
    {
      isActive,
      tipo,
      tituloAba,
      descricaoGeral,
      secoes,
      solicitacaoId,
      onProximo,
      onStatusChange,
      onSaveAll,
      submitLabel = "PRÓXIMO",
      submitDisabled = false,
      onEnviarPreConfirmacao,
      readOnly = false,
      parecerPorSecaoId,
      dadosIndicadoresAnalise = null,
      analiseCamposPorSecaoId,
      analisePodeEditar = true,
      onAnaliseCamposChange,
      receitaOperacionalReferencia,
      onReceitaOperacionalReferenciaChange,
      associadosReferencia,
      onAssociadosReferenciaChange,
      analiseAguardandoRetornoPorSecaoId,
      analiseBloqueadaPorAdequacaoGlobal = false,
      historicoAdequacaoPorAnaliseKey,
      ciclosHistoricoAdequacaoPorAnaliseKey,
    },
    ref,
  ) => {
    const [respostas, setRespostas] = useState<Record<string, string>>({});
    const [arquivos, setArquivos] = useState<Record<string, string>>({});
    const [arquivosIdsPorSecao, setArquivosIdsPorSecao] = useState<Record<string, number[]>>(
      {},
    );
    const [downloadLoadingKey, setDownloadLoadingKey] = useState<string | null>(null);
    const [uploadProcessingKey, setUploadProcessingKey] = useState<string | null>(null);
    const [historicoAdequacaoAbertoPorSecaoId, setHistoricoAdequacaoAbertoPorSecaoId] =
      useState<Record<string, boolean>>({});

    const initialPerguntaValorBySecao = React.useRef<Record<string, string>>({});
    const initialArquivosIdsBySecao = React.useRef<Record<string, number[]>>({});
    const initialObservacaoBySecao = React.useRef<Record<string, string>>({});
    const initialObsSindicatoByAnaliseKeyRef = React.useRef<Record<string, string>>({});
    const initialAdimplenciaRef = React.useRef<AdimplenciaMeta["initial"] | null>(null);

    const nomeArquivoOriginalClienteRef = React.useRef<Record<string, string>>({});

    useEffect(() => {
      nomeArquivoOriginalClienteRef.current = {};
    }, [solicitacaoId]);

    useEffect(() => {
      setDownloadLoadingKey(null);
      setUploadProcessingKey(null);
    }, [solicitacaoId]);

    const secoesSyncKey = useMemo(
      () =>
        `${solicitacaoId ?? "none"}|${secoes
          .map((s) => {
            if (s.id === "adimplencia" && s.adimplenciaMeta?.initial) {
              const i = s.adimplenciaMeta.initial;
              const assocSnap = i.associados
                .map(
                  (a) =>
                    `${(a.arquivosIds ?? []).join(",")}:${a.arquivoNome ?? ""}:${a.arquivoNomeExibicao ?? ""}:${a.cnpj}:${a.nomeSocial}`,
                )
                .join(";");
              return `adimpl:${i.formaCobrancaValorUI}:${i.quantidadeAssociados}:${i.tipoDocumentoValorUI}:${i.observacaoGeral}:${assocSnap}`;
            }
            return `${s.id}:${s.initialPerguntaValor ?? ""}:${(s.initialArquivosIds ?? []).join(",")}:${s.initialArquivoName ?? ""}:${s.initialArquivoNomeExibicao ?? ""}:${s.initialObservacao ?? ""}`;
          })
          .join("||")}`,
      [secoes, solicitacaoId],
    );

    const prevSecoesSyncKeyRef = React.useRef<string>("");
    const [observacoes, setObservacoes] = useState<Record<string, string>>({});
    const [acaoToast, setAcaoToast] = useState<{
      show: boolean;
      message: string;
      type: "success" | "error";
    }>({ show: false, message: "", type: "error" });
    const [analiseCampos, setAnaliseCampos] = useState<
      Record<string, AnaliseCamposPorSecao>
    >({});
    const [obsSindicatoByAnaliseKey, setObsSindicatoByAnaliseKey] = useState<
      Record<string, string>
    >({});

    const [openBySection, setOpenBySection] = useState<Record<string, boolean>>(() => {
      const initial: Record<string, boolean> = {};
      secoes.forEach((secao) => {
        initial[secao.id] = false;
      });
      return initial;
    });

    useEffect(() => {
      if (secoes.length === 0) return;
      if (secoesSyncKey === prevSecoesSyncKeyRef.current) return;
      prevSecoesSyncKeyRef.current = secoesSyncKey;

      const nextRespostas: Record<string, string> = {};
      const nextArquivos: Record<string, string> = {};
      const nextArquivosIds: Record<string, number[]> = {};
      const nextObservacoes: Record<string, string> = {};

      secoes.forEach((secao) => {
        if (secao.id === "adimplencia" && secao.adimplenciaMeta?.initial) {
          const meta = secao.adimplenciaMeta;
          const initial = meta.initial;

          nextRespostas["adimplencia-formaCobranca"] = initial.formaCobrancaValorUI ?? "";
          nextRespostas["adimplencia-quantidade"] =
            initial.quantidadeAssociados > 0 ? String(initial.quantidadeAssociados) : "";
          nextRespostas["adimplencia-tipoDocumento"] = initial.tipoDocumentoValorUI ?? "";

          nextObservacoes["adimplencia"] = initial.observacaoGeral ?? "";

          for (let i = 0; i < meta.associados.length; i++) {
            const assocInitial = initial.associados[i];
            nextRespostas[`adimplencia-assoc-${i}-cnpj`] = assocInitial?.cnpj ?? "";
            nextRespostas[`adimplencia-assoc-${i}-nomeSocial`] = assocInitial?.nomeSocial ?? "";
            nextObservacoes[`adimplencia-assoc-${i}`] = assocInitial?.observacao ?? "";

            const chaveAssoc = `adimplencia-assoc-${i}`;
            const ids = assocInitial?.arquivosIds ?? [];
            nextArquivosIds[chaveAssoc] = ids;
            const nomeApiAssoc = String(assocInitial?.arquivoNome ?? "").trim();
            const nomeExibicaoAssoc = String(assocInitial?.arquivoNomeExibicao ?? "").trim();
            const prefAssoc = nomeArquivoOriginalClienteRef.current[chaveAssoc]?.trim();
            if (ids.length > 0) {
              nextArquivos[chaveAssoc] = prefAssoc || nomeExibicaoAssoc || nomeApiAssoc || "";
            } else {
              delete nomeArquivoOriginalClienteRef.current[chaveAssoc];
            }
          }

          initialAdimplenciaRef.current = initial;
          return;
        }

        if (
          (secao.tipo === "pergunta" || secao.tipo === "pergunta-com-upload") &&
          typeof secao.initialPerguntaValor === "string"
        ) {
          nextRespostas[secao.id] = secao.initialPerguntaValor;
        }

        if (secao.tipo === "upload" || secao.tipo === "pergunta-com-upload") {
          const idsArq = secao.initialArquivosIds ?? [];
          const nomeApi = String(secao.initialArquivoName ?? "").trim();
          const nomeExibicao = String(secao.initialArquivoNomeExibicao ?? "").trim();
          const nomePref = nomeArquivoOriginalClienteRef.current[secao.id]?.trim();
          nextArquivosIds[secao.id] = idsArq;
          nextObservacoes[secao.id] = secao.initialObservacao ?? "";
          if (idsArq.length > 0) {
            nextArquivos[secao.id] = nomePref || nomeExibicao || nomeApi || "";
          } else {
            nextArquivos[secao.id] = "";
            delete nomeArquivoOriginalClienteRef.current[secao.id];
          }
        }
      });

      setRespostas(nextRespostas);
      setArquivos(nextArquivos);
      setArquivosIdsPorSecao(nextArquivosIds);
      setObservacoes((prev) => ({ ...prev, ...nextObservacoes }));

      const initPergunta: Record<string, string> = {};
      const initArquivosIds: Record<string, number[]> = {};
      const initObservacoes: Record<string, string> = {};

      secoes.forEach((secao) => {
        if (secao.tipo === "pergunta" || secao.tipo === "pergunta-com-upload") {
          initPergunta[secao.id] = secao.initialPerguntaValor ?? "";
        }

        if (secao.tipo === "upload" || secao.tipo === "pergunta-com-upload") {
          initArquivosIds[secao.id] = secao.initialArquivosIds ?? [];
          initObservacoes[secao.id] = secao.initialObservacao ?? "";
        }
      });

      initialPerguntaValorBySecao.current = initPergunta;
      initialArquivosIdsBySecao.current = initArquivosIds;
      initialObservacaoBySecao.current = initObservacoes;
    }, [secoes, secoesSyncKey]);

    useEffect(() => {
      setOpenBySection((prev) => {
        let changed = false;
        const next = { ...prev };

        secoes.forEach((secao) => {
          if (next[secao.id] === undefined) {
            next[secao.id] = false;
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    }, [secoes]);

    useEffect(() => {
      const raw = analiseCamposPorSecaoId ?? {};
      const normalized = normalizarAnaliseDevolverSomenteAnalista(raw, secoes);
      if (normalized !== raw) {
        setAnaliseCampos(normalized);
        onAnaliseCamposChange?.(normalized);
      } else {
        setAnaliseCampos(raw);
      }
    }, [analiseCamposPorSecaoId, secoes, onAnaliseCamposChange]);

    const allSectionIds = useMemo(() => secoes.map((secao) => secao.id), [secoes]);

    const isAllOpen =
      allSectionIds.length > 0 &&
      allSectionIds.every((id) => openBySection[id] === true);

    const toggleAll = useCallback(() => {
      const ids = secoes.map((secao) => secao.id);

      setOpenBySection((prev) => {
        const currentlyAllOpen = ids.length > 0 && ids.every((id) => prev[id] === true);

        const nextValue = !currentlyAllOpen;
        const next = { ...prev };

        ids.forEach((id) => {
          next[id] = nextValue;
        });

        return next;
      });
    }, [secoes]);

    const handleToggleOne = (secaoId: string) => {
      setOpenBySection((prev) => ({
        ...prev,
        [secaoId]: !prev[secaoId],
      }));
    };

    const montarCamposAlterados = useCallback((): RequestRespostaCampoJson[] => {
      const campos: RequestRespostaCampoJson[] = [];

      const compareArray = (a: number[] = [], b: number[] = []) =>
        JSON.stringify(a) !== JSON.stringify(b);

      const adimplenciaSecao = secoes.find((s) => s.id === "adimplencia");
      const adimplenciaMeta = adimplenciaSecao?.adimplenciaMeta;
      const initialAdimplencia = initialAdimplenciaRef.current;

      if (adimplenciaMeta && initialAdimplencia) {
        const currentFormaCobranca = respostas["adimplencia-formaCobranca"] ?? "";
        const currentObservacaoGeral = observacoes["adimplencia"] ?? "";
        const currentQuantidade = Math.max(
          0,
          Math.min(
            adimplenciaMeta.maxAssociados,
            parseInt(respostas["adimplencia-quantidade"] ?? "0", 10) || 0,
          ),
        );
        const currentTipoDocumento = respostas["adimplencia-tipoDocumento"] ?? "";

        const opcaoIdForma =
          adimplenciaMeta.campoFormaCobranca.opcaoIdByValorUI?.[currentFormaCobranca] ??
          adimplenciaMeta.campoFormaCobranca.opcaoIdByDescricao[currentFormaCobranca];

        const backendDescricaoTipoDocumento =
          adimplenciaMeta.campoTipoDocumento.descricaoBackendByValorUI[currentTipoDocumento] ??
          "";

        const opcaoIdTipoDocumento = backendDescricaoTipoDocumento
          ? adimplenciaMeta.campoTipoDocumento.opcaoIdByDescricaoBackend[
          backendDescricaoTipoDocumento
          ]
          : undefined;

        if (
          initialAdimplencia.formaCobrancaValorUI !== currentFormaCobranca &&
          adimplenciaMeta.campoFormaCobranca.campoId != null
        ) {
          const hasOpcaoForma = typeof opcaoIdForma === "number";
          campos.push({
            campoId: adimplenciaMeta.campoFormaCobranca.campoId,
            valorTexto: currentFormaCobranca ?? "",
            ...(hasOpcaoForma
              ? {
                opcaoCampoId: opcaoIdForma,
                opcoesSelecionadasIds: [opcaoIdForma],
              }
              : {}),
          });
        }

        if (
          initialAdimplencia.observacaoGeral !== (currentObservacaoGeral ?? "") &&
          adimplenciaMeta.campoObservacaoGeralId != null
        ) {
          campos.push({
            campoId: adimplenciaMeta.campoObservacaoGeralId,
            valorTexto: currentObservacaoGeral ?? "",
          });
        }

        if (
          initialAdimplencia.quantidadeAssociados !== currentQuantidade &&
          adimplenciaMeta.campoQuantidade.campoId != null
        ) {
          const opcaoIdQuantidade =
            adimplenciaMeta.campoQuantidade.opcaoIdByValorUI?.[String(currentQuantidade)] ??
            adimplenciaMeta.campoQuantidade.opcaoIdByDescricao[String(currentQuantidade)];

          const hasOpcaoQuantidade = typeof opcaoIdQuantidade === "number";
          campos.push({
            campoId: adimplenciaMeta.campoQuantidade.campoId,
            valorInteiro: currentQuantidade,
            ...(hasOpcaoQuantidade
              ? {
                opcaoCampoId: opcaoIdQuantidade,
                opcoesSelecionadasIds: [opcaoIdQuantidade],
              }
              : {}),
          });
        }

        if (
          initialAdimplencia.tipoDocumentoValorUI !== currentTipoDocumento &&
          adimplenciaMeta.campoTipoDocumento.campoId != null
        ) {
          const hasOpcaoTipoDoc = typeof opcaoIdTipoDocumento === "number";
          campos.push({
            campoId: adimplenciaMeta.campoTipoDocumento.campoId,
            valorTexto: backendDescricaoTipoDocumento,
            ...(hasOpcaoTipoDoc
              ? {
                opcaoCampoId: opcaoIdTipoDocumento,
                opcoesSelecionadasIds: [opcaoIdTipoDocumento],
              }
              : {}),
          });
        }

        const compareArrayIds = (a: number[] = [], b: number[] = []) =>
          JSON.stringify(a) !== JSON.stringify(b);

        for (let i = 0; i < currentQuantidade; i++) {
          const assocMeta = adimplenciaMeta.associados[i];
          const assocBaseline = initialAdimplencia.associados[i];

          if (!assocMeta) continue;

          const currentCnpj = respostas[`adimplencia-assoc-${i}-cnpj`] ?? "";
          const currentNomeSocial = respostas[`adimplencia-assoc-${i}-nomeSocial`] ?? "";
          const currentObs = observacoes[`adimplencia-assoc-${i}`] ?? "";
          const currentArquivoIds = arquivosIdsPorSecao[`adimplencia-assoc-${i}`] ?? [];

          const baselineCnpj = assocBaseline?.cnpj ?? "";
          const baselineNomeSocial = assocBaseline?.nomeSocial ?? "";
          const baselineObs = assocBaseline?.observacao ?? "";
          const baselineArquivoIds = assocBaseline?.arquivosIds ?? [];

          if (baselineCnpj !== currentCnpj) {
            campos.push({
              campoId: assocMeta.campoCnpjId,
              valorTexto: currentCnpj ?? "",
            });
          }

          if (baselineNomeSocial !== currentNomeSocial) {
            campos.push({
              campoId: assocMeta.campoRazaoId,
              valorTexto: currentNomeSocial ?? "",
            });
          }

          if (baselineObs !== currentObs) {
            campos.push({
              campoId: assocMeta.campoObservacaoId,
              valorTexto: currentObs ?? "",
            });
          }

          if (compareArrayIds(baselineArquivoIds, currentArquivoIds)) {
            campos.push({
              campoId: assocMeta.campoUploadId,
              arquivosIds: currentArquivoIds,
            });
          }
        }
      }

      secoes.forEach((secao) => {
        if (secao.tipo === "somente-analista") {
          return;
        }

        if (
          (secao.tipo === "pergunta" || secao.tipo === "pergunta-com-upload") &&
          secao.campoPerguntaId != null
        ) {
          const baselineValor = initialPerguntaValorBySecao.current[secao.id] ?? "";
          const currentValor = respostas[secao.id] ?? "";

          if (baselineValor !== currentValor) {
            const selectedOpcaoIds =
              (secao.opcoes ?? [])
                .filter((o) => {
                  if (String(o.valor) === String(currentValor)) return true;
                  if (secaoTemApenasOpcoesSimNao(secao)) {
                    const a = normalizarValorSimNaoParaUI(o.valor);
                    const b = normalizarValorSimNaoParaUI(currentValor);
                    return a !== "" && a === b;
                  }
                  return false;
                })
                .map((o) => o.id)
                .filter((id): id is number => typeof id === "number") ?? [];

            if (selectedOpcaoIds.length > 0) {
              campos.push({
                campoId: secao.campoPerguntaId,
                valorTexto: currentValor,
                opcaoCampoId: selectedOpcaoIds[0] ?? null,
                opcoesSelecionadasIds: selectedOpcaoIds,
              });
            } else if (selectedOpcaoIds.length === 0) {
              // Backend pode retornar opções SIM/NÃO sem `id` (ex.: alguns critérios).
              // Nesses casos, precisamos persistir a resposta via `valorTexto`
              // mesmo sem `opcaoCampoId`.
              if (secaoTemApenasOpcoesSimNao(secao) && String(currentValor).trim()) {
                campos.push({
                  campoId: secao.campoPerguntaId,
                  valorTexto: currentValor,
                });
              }
            }
          }
        }

        if (
          (secao.tipo === "upload" || secao.tipo === "pergunta-com-upload") &&
          secao.campoUploadId != null
        ) {
          const perguntaComUploadNao =
            secao.tipo === "pergunta-com-upload" &&
            normalizarValorSimNaoParaUI(respostas[secao.id] ?? "") === "NÃO";

          const baselineArquivosIds = initialArquivosIdsBySecao.current[secao.id] ?? [];
          const currentArquivosIds = arquivosIdsPorSecao[secao.id] ?? [];
          const effectiveArquivosIds = perguntaComUploadNao ? [] : currentArquivosIds;

          if (compareArray(baselineArquivosIds, effectiveArquivosIds)) {
            campos.push({
              campoId: secao.campoUploadId,
              arquivosIds: effectiveArquivosIds,
            });
          }
        }

        if (
          (secao.tipo === "upload" || secao.tipo === "pergunta-com-upload") &&
          secao.campoObservacaoId != null
        ) {
          const perguntaComUploadNao =
            secao.tipo === "pergunta-com-upload" &&
            normalizarValorSimNaoParaUI(respostas[secao.id] ?? "") === "NÃO";

          const baselineObs = String(secao.initialObservacao ?? "");
          const baselineObsRef = String(initialObservacaoBySecao.current[secao.id] ?? baselineObs);
          const currentObs = perguntaComUploadNao
            ? ""
            : String(observacoes[secao.id] ?? "");
          if (baselineObsRef !== currentObs) {
            campos.push({
              campoId: secao.campoObservacaoId,
              valorTexto: currentObs,
            });
          }
        }
      });

      return campos;
    }, [secoes, respostas, arquivosIdsPorSecao, observacoes]);

    const isDirty = useCallback(() => montarCamposAlterados().length > 0, [montarCamposAlterados]);

    const markAsSaved = useCallback(() => {
      const adimplenciaMeta = secoes.find((s) => s.id === "adimplencia")?.adimplenciaMeta;

      secoes.forEach((secao) => {
        if (
          (secao.tipo === "pergunta" || secao.tipo === "pergunta-com-upload") &&
          secao.campoPerguntaId != null
        ) {
          initialPerguntaValorBySecao.current[secao.id] = respostas[secao.id] ?? "";
        }

        if (
          (secao.tipo === "upload" || secao.tipo === "pergunta-com-upload") &&
          secao.campoUploadId != null
        ) {
          initialArquivosIdsBySecao.current[secao.id] =
            arquivosIdsPorSecao[secao.id] ?? [];
        }

        if (
          (secao.tipo === "upload" || secao.tipo === "pergunta-com-upload") &&
          secao.campoObservacaoId != null
        ) {
          initialObservacaoBySecao.current[secao.id] = observacoes[secao.id] ?? "";
        }
      });

      if (adimplenciaMeta && initialAdimplenciaRef.current) {
        const currentFormaCobranca = respostas["adimplencia-formaCobranca"] ?? "";
        const currentObservacaoGeral = observacoes["adimplencia"] ?? "";
        const currentQuantidade = Math.max(
          0,
          Math.min(
            adimplenciaMeta.maxAssociados,
            parseInt(respostas["adimplencia-quantidade"] ?? "0", 10) || 0,
          ),
        );
        const currentTipoDocumento = respostas["adimplencia-tipoDocumento"] ?? "";

        const baselineAssociados = initialAdimplenciaRef.current.associados ?? [];
        const currentAssociados = Array.from(
          { length: adimplenciaMeta.associados.length },
          (_, i) => {
            if (i >= currentQuantidade) {
              return (
                baselineAssociados[i] ?? {
                  cnpj: "",
                  nomeSocial: "",
                  observacao: "",
                  arquivosIds: [],
                }
              );
            }

            return {
              cnpj: respostas[`adimplencia-assoc-${i}-cnpj`] ?? "",
              nomeSocial: respostas[`adimplencia-assoc-${i}-nomeSocial`] ?? "",
              observacao: observacoes[`adimplencia-assoc-${i}`] ?? "",
              arquivosIds: arquivosIdsPorSecao[`adimplencia-assoc-${i}`] ?? [],
              arquivoNome: arquivos[`adimplencia-assoc-${i}`] ?? "",
            };
          },
        );

        initialAdimplenciaRef.current = {
          ...initialAdimplenciaRef.current,
          formaCobrancaValorUI: currentFormaCobranca,
          observacaoGeral: currentObservacaoGeral,
          quantidadeAssociados: currentQuantidade,
          tipoDocumentoValorUI: currentTipoDocumento,
          associados: currentAssociados,
        };
      }
      initialObsSindicatoByAnaliseKeyRef.current = { ...obsSindicatoByAnaliseKey };
    }, [secoes, respostas, arquivosIdsPorSecao, observacoes, arquivos, obsSindicatoByAnaliseKey]);

    const handleSave = useCallback(async () => {
      if (uploadProcessingKey) {
        return {
          show: true,
          message: "Aguarde o término do upload antes de salvar.",
          type: "error",
        };
      }

      if (!solicitacaoId) {
        return {
          show: true,
          message: "Não foi possível salvar: solicitacaoId não informado.",
          type: "error",
        };
      }

      const campos = montarCamposAlterados();

      if (campos.length === 0) {
        return {
          show: true,
          message: "Salvo com sucesso!",
          type: "success",
        };
      }

      await patchAvaliacaoSolicitacao(solicitacaoId, {
        campos,
      });

      markAsSaved();

      return {
        show: true,
        message: "Documentos salvos com sucesso!",
        type: "success",
      };
    }, [solicitacaoId, montarCamposAlterados, markAsSaved, uploadProcessingKey]);

    const handleSubmit = async () => {
      if (uploadProcessingKey) {
        return {
          show: true,
          message: "Aguarde o término do upload antes de continuar.",
          type: "error",
        };
      }

      if (onEnviarPreConfirmacao) {
        return onEnviarPreConfirmacao();
      }
      onProximo?.();
    };

    const handlePerguntaChange = (secaoId: string, valor: string) => {
      setRespostas((prev) => ({
        ...prev,
        [secaoId]: valor,
      }));
    };

    const handleUploadEscolher = (_secaoId: string) => {
      // não cria mais placeholder fake do tipo Documento-3.pdf
    };

    const handleDownloadArquivoPorSecao = useCallback(
      async (secaoId: string, nomeExibicao?: string | null) => {
        const ids = arquivosIdsPorSecao[secaoId] ?? [];
        const arquivoRespostaCampoId = ids[0];
        if (typeof arquivoRespostaCampoId !== "number" || arquivoRespostaCampoId <= 0) {
          return;
        }
        const nome =
          String(nomeExibicao ?? "")
            .trim()
            .replace(/[/\\?%*:|"<>]/g, "_") || "arquivo";
        setDownloadLoadingKey(secaoId);
        try {
          await downloadArquivoRespostaCampo(arquivoRespostaCampoId, nome);
        } finally {
          setDownloadLoadingKey((k) => (k === secaoId ? null : k));
        }
      },
      [arquivosIdsPorSecao],
    );

    const resolveCampoUploadId = useCallback(
      (secaoId: string): number | null => {
        if (secaoId.startsWith("adimplencia-assoc-")) {
          const idx = Number(secaoId.replace("adimplencia-assoc-", ""));
          const adimplenciaSecao = secoes.find((s) => s.id === "adimplencia");
          const meta = adimplenciaSecao?.adimplenciaMeta;
          const campoId = meta?.associados?.[idx]?.campoUploadId;
          return typeof campoId === "number" ? campoId : null;
        }

        const secao = secoes.find((s) => s.id === secaoId);
        return typeof secao?.campoUploadId === "number" ? secao.campoUploadId : null;
      },
      [secoes],
    );

    const removerArquivoNoBackend = useCallback(
      async (secaoId: string): Promise<boolean> => {
        const currentIds = arquivosIdsPorSecao[secaoId] ?? [];

        try {
          if (currentIds.length > 0) {
            await Promise.all(currentIds.map((id) => deleteArquivoRespostaCampo(id)));
            return true;
          }

          if (solicitacaoId != null) {
            const campoUploadId = resolveCampoUploadId(secaoId);
            if (campoUploadId != null) {
              await patchAvaliacaoSolicitacao(solicitacaoId, {
                campos: [
                  {
                    campoId: campoUploadId,
                    arquivosIds: [],
                  },
                ],
              });
            }
          }

          return true;
        } catch {
          return false;
        }
      },
      [arquivosIdsPorSecao, solicitacaoId, resolveCampoUploadId],
    );

    const isArquivoAtivoError = (error: unknown) => {
      const maybeResponseData =
        (error as any)?.response?.data ?? (error as any)?.data ?? (error as any)?.message;
      const serialized =
        typeof maybeResponseData === "string"
          ? maybeResponseData
          : JSON.stringify(maybeResponseData ?? "");
      return String(serialized).toLowerCase().includes("já existe um arquivo ativo");
    };

    const handleFileSelected = async (
      secaoId: string,
      file: File,
      options?: { isReplace?: boolean },
    ) => {
      if (readOnly) return;
      const secao = secoes.find((s) => s.id === secaoId);
      if (!secao?.campoUploadId || solicitacaoId == null) return;

      setUploadProcessingKey(secaoId);
      try {
        setArquivos((prev) => ({ ...prev, [secaoId]: file.name }));

        const uploaded = await uploadArquivoRespostaCampo({
          solicitacaoId,
          campoId: secao.campoUploadId,
          file,
        });

        const arquivoId = extrairArquivoRespostaCampoId(uploaded);
        if (arquivoId == null) {
          setArquivos((prev) => {
            const next = { ...prev };
            delete next[secaoId];
            return next;
          });
          setArquivosIdsPorSecao((prev) => ({ ...prev, [secaoId]: [] }));
          setAcaoToast({
            show: true,
            type: "error",
            message:
              "O servidor não retornou o identificador do arquivo. Tente anexar novamente ou salve após recarregar a página.",
          });
          return;
        }

        setArquivosIdsPorSecao((prev) => ({
          ...prev,
          [secaoId]: [arquivoId],
        }));
        nomeArquivoOriginalClienteRef.current[secaoId] = file.name;
        if (options?.isReplace) {
          setAcaoToast({
            show: true,
            type: "success",
            message: "Arquivo alterado com sucesso.",
          });
        }
      } catch (error) {
        // Integração real: se o backend indicar arquivo ativo, força limpeza e tenta uma vez novamente.
        if (isArquivoAtivoError(error)) {
          const removed = await removerArquivoNoBackend(secaoId);
          if (removed) {
            try {
              const uploaded = await uploadArquivoRespostaCampo({
                solicitacaoId,
                campoId: secao.campoUploadId,
                file,
              });
              const retryId = extrairArquivoRespostaCampoId(uploaded);
              setArquivos((prev) => ({ ...prev, [secaoId]: file.name }));
              setArquivosIdsPorSecao((prev) => ({
                ...prev,
                [secaoId]: retryId != null ? [retryId] : [],
              }));
              if (retryId == null) {
                setArquivos((prev) => {
                  const next = { ...prev };
                  delete next[secaoId];
                  return next;
                });
                setAcaoToast({
                  show: true,
                  type: "error",
                  message:
                    "O servidor não retornou o identificador do arquivo após nova tentativa.",
                });
                return;
              }
              nomeArquivoOriginalClienteRef.current[secaoId] = file.name;
              setAcaoToast({
                show: true,
                type: "success",
                message: "Arquivo alterado com sucesso.",
              });
              return;
            } catch {}
          }
        }

        setAcaoToast({
          show: true,
          type: "error",
          message: "Não foi possível alterar o arquivo. Tente novamente.",
        });

        setArquivos((prev) => {
          const next = { ...prev };
          delete next[secaoId];
          return next;
        });

        setArquivosIdsPorSecao((prev) => ({
          ...prev,
          [secaoId]: [],
        }));
      } finally {
        setUploadProcessingKey((k) => (k === secaoId ? null : k));
      }
    };

    const handleUploadRemover = async (secaoId: string) => {
      if (readOnly) return;
      const removed = await removerArquivoNoBackend(secaoId);
      if (!removed) {
        // Não limpa UI quando o backend falha, evitando falso estado de "arquivo removido".
        setAcaoToast({
          show: true,
          type: "error",
          message:
            "Não foi possível remover o arquivo. O servidor retornou erro — tente novamente ou contate o suporte.",
        });
        return;
      }

      setArquivos((prev) => {
        const next = { ...prev };
        delete next[secaoId];
        return next;
      });

      setArquivosIdsPorSecao((prev) => ({ ...prev, [secaoId]: [] }));

      // Alinha baseline com o servidor após exclusão (evita comparar id antigo no PATCH).
      if (secaoId.startsWith("adimplencia-assoc-")) {
        const idx = Number(secaoId.replace("adimplencia-assoc-", ""));
        const init = initialAdimplenciaRef.current;
        if (init?.associados && Number.isFinite(idx) && idx >= 0 && init.associados[idx]) {
          initialAdimplenciaRef.current = {
            ...init,
            associados: init.associados.map((a, i) =>
              i === idx ? { ...a, arquivosIds: [], arquivoNome: undefined } : a,
            ),
          };
        }
      } else {
        initialArquivosIdsBySecao.current[secaoId] = [];
      }

      delete nomeArquivoOriginalClienteRef.current[secaoId];

      setAcaoToast({
        show: true,
        type: "success",
        message: "Arquivo removido com sucesso.",
      });
    };

    const handleObservacaoChange = (secaoId: string, value: string) => {
      setObservacoes((prev) => ({
        ...prev,
        [secaoId]: value,
      }));
    };

    const getAnaliseKeyFromSecao = useCallback((secao: SecaoDocumento): string => {
      const criterioId = secao.criterioSolicitacaoId;
      return typeof criterioId === "number" && Number.isFinite(criterioId)
        ? String(criterioId)
        : secao.id;
    }, []);

    const getAnaliseKeyBySecaoId = useCallback(
      (secaoId: string): string => {
        const secao = secoes.find((s) => s.id === secaoId);
        return secao ? getAnaliseKeyFromSecao(secao) : secaoId;
      },
      [secoes, getAnaliseKeyFromSecao],
    );

    useEffect(() => {
      const next: Record<string, string> = {};
      secoes.forEach((secao) => {
        const analiseKey = getAnaliseKeyFromSecao(secao);
        next[analiseKey] = String(
          historicoAdequacaoPorAnaliseKey?.[analiseKey]?.observacaoSindicato ?? "",
        );
      });
      setObsSindicatoByAnaliseKey(next);
      initialObsSindicatoByAnaliseKeyRef.current = { ...next };
    }, [secoesSyncKey, historicoAdequacaoPorAnaliseKey, secoes, getAnaliseKeyFromSecao]);

    const montarAnaliseCriterioSolicitacaoAlterados = useCallback(
      (): Array<{ id: number; criterioId: number; observacaoSindicato?: string | null }> => {
        if (readOnly) return [];
        const out: Array<{
          id: number;
          criterioId: number;
          observacaoSindicato?: string | null;
        }> = [];
        for (const secao of secoes) {
          const analiseKey = getAnaliseKeyFromSecao(secao);
          const histAdequ = historicoAdequacaoPorAnaliseKey?.[analiseKey];
          if (!histAdequ?.solicitadoEm || histAdequ.adequadoEm) continue;
          if (secao.canEditObservacao === false) continue;
          const baseline = String(
            initialObsSindicatoByAnaliseKeyRef.current[analiseKey] ?? "",
          );
          const current = String(obsSindicatoByAnaliseKey[analiseKey] ?? "");
          if (baseline === current) continue;
          out.push({
            id:
              typeof histAdequ.id === "number" && Number.isFinite(histAdequ.id)
                ? histAdequ.id
                : 0,
            criterioId:
              typeof secao.criterioSolicitacaoId === "number" &&
              Number.isFinite(secao.criterioSolicitacaoId)
                ? secao.criterioSolicitacaoId
                : Number(analiseKey) || 0,
            observacaoSindicato: current.slice(0, 10000),
          });
        }
        return out;
      },
      [
        readOnly,
        secoes,
        getAnaliseKeyFromSecao,
        historicoAdequacaoPorAnaliseKey,
        obsSindicatoByAnaliseKey,
      ],
    );

    const upsertAnaliseSecao = useCallback(
      (
        secaoId: string,
        patch: Partial<AnaliseCamposPorSecao>,
      ): Record<string, AnaliseCamposPorSecao> => {
        const secao = secoes.find((s) => s.id === secaoId);
        const analiseKey = getAnaliseKeyBySecaoId(secaoId);
        const atual = analiseCampos[analiseKey] ?? {
          status: "",
          observacaoAnalista: "",
          observacaoInterna: "",
        };
        const merged: AnaliseCamposPorSecao = { ...atual, ...patch };

        
        const chaves = new Set<string>([analiseKey, secaoId]);
        if (
          typeof merged.criterioIdApi === "number" &&
          Number.isFinite(merged.criterioIdApi) &&
          merged.criterioIdApi > 0
        ) {
          chaves.add(String(merged.criterioIdApi));
        }
        if (
          secao &&
          typeof secao.criterioSolicitacaoId === "number" &&
          Number.isFinite(secao.criterioSolicitacaoId)
        ) {
          chaves.add(String(secao.criterioSolicitacaoId));
        }

        const nextMap = { ...analiseCampos };
        for (const k of chaves) {
          nextMap[k] = { ...merged };
        }
        setAnaliseCampos(nextMap);
        onAnaliseCamposChange?.(nextMap);
        return nextMap;
      },
      [analiseCampos, onAnaliseCamposChange, getAnaliseKeyBySecaoId, secoes],
    );

    const handleAnaliseStatusChange = (secaoId: string, status: AnaliseStatusUI) => {
      upsertAnaliseSecao(secaoId, { status });
    };

    const handleAnaliseObsAnalistaChange = (secaoId: string, value: string) => {
      upsertAnaliseSecao(secaoId, { observacaoAnalista: value.slice(0, 10000) });
    };

    const handleAnaliseObsInternaChange = (secaoId: string, value: string) => {
      upsertAnaliseSecao(secaoId, { observacaoInterna: value.slice(0, 10000) });
    };

    const handleAdimplenciaFileSelected = async (
      index: number,
      file: File,
      options?: { isReplace?: boolean },
    ) => {
      if (readOnly) return;
      if (solicitacaoId == null) return;

      const adimplenciaSecao = secoes.find((s) => s.id === "adimplencia");
      const meta = adimplenciaSecao?.adimplenciaMeta;
      const assocMeta = meta?.associados?.[index];
      const campoUploadId = assocMeta?.campoUploadId;
      if (campoUploadId == null) return;

      const chave = `adimplencia-assoc-${index}`;

      setUploadProcessingKey(chave);
      try {
        setArquivos((prev) => ({ ...prev, [chave]: file.name }));

        const uploaded = await uploadArquivoRespostaCampo({
          solicitacaoId,
          campoId: campoUploadId,
          file,
        });

        const arquivoAssocId = extrairArquivoRespostaCampoId(uploaded);
        if (arquivoAssocId == null) {
          setArquivos((prev) => {
            const next = { ...prev };
            delete next[chave];
            return next;
          });
          setArquivosIdsPorSecao((prev) => ({ ...prev, [chave]: [] }));
          setAcaoToast({
            show: true,
            type: "error",
            message:
              "O servidor não retornou o identificador do arquivo. Tente anexar novamente.",
          });
          return;
        }

        setArquivosIdsPorSecao((prev) => ({
          ...prev,
          [chave]: [arquivoAssocId],
        }));
        nomeArquivoOriginalClienteRef.current[chave] = file.name;
        if (options?.isReplace) {
          setAcaoToast({
            show: true,
            type: "success",
            message: "Arquivo alterado com sucesso.",
          });
        }
      } catch (error) {
        if (isArquivoAtivoError(error)) {
          const removed = await removerArquivoNoBackend(chave);
          if (removed) {
            try {
              const uploaded = await uploadArquivoRespostaCampo({
                solicitacaoId,
                campoId: campoUploadId,
                file,
              });
              const retryAssocId = extrairArquivoRespostaCampoId(uploaded);
              setArquivos((prev) => ({ ...prev, [chave]: file.name }));
              setArquivosIdsPorSecao((prev) => ({
                ...prev,
                [chave]: retryAssocId != null ? [retryAssocId] : [],
              }));
              if (retryAssocId == null) {
                setArquivos((prev) => {
                  const next = { ...prev };
                  delete next[chave];
                  return next;
                });
                setAcaoToast({
                  show: true,
                  type: "error",
                  message:
                    "O servidor não retornou o identificador do arquivo após nova tentativa.",
                });
                return;
              }
              nomeArquivoOriginalClienteRef.current[chave] = file.name;
              setAcaoToast({
                show: true,
                type: "success",
                message: "Arquivo alterado com sucesso.",
              });
              return;
            } catch {}
          }
        }

        setAcaoToast({
          show: true,
          type: "error",
          message: "Não foi possível alterar o arquivo. Tente novamente.",
        });

        setArquivos((prev) => {
          const next = { ...prev };
          delete next[chave];
          return next;
        });

        setArquivosIdsPorSecao((prev) => ({
          ...prev,
          [chave]: [],
        }));
      } finally {
        setUploadProcessingKey((k) => (k === chave ? null : k));
      }
    };

    const quantidadeAssociados = Math.min(
      10,
      Math.max(0, parseInt(respostas["adimplencia-quantidade"] ?? "0", 10) || 0),
    );

    const associadosAdimplencia = Array.from({ length: 10 }, (_, index) => ({
      cnpj: respostas[`adimplencia-assoc-${index}-cnpj`] ?? "",
      nomeSocial: respostas[`adimplencia-assoc-${index}-nomeSocial`] ?? "",
      observacao: observacoes[`adimplencia-assoc-${index}`] ?? "",
    }));

    const arquivosAdimplenciaAssoc: Record<number, string | null> = {};
    const observacoesAdimplenciaAssoc: Record<number, string> = {};

    for (let i = 0; i < 10; i++) {
      arquivosAdimplenciaAssoc[i] = arquivos[`adimplencia-assoc-${i}`] ?? null;
      observacoesAdimplenciaAssoc[i] = observacoes[`adimplencia-assoc-${i}`] ?? "";
    }

    const handleAdimplenciaAssociadoChange = (
      index: number,
      field: "cnpj" | "nomeSocial",
      value: string,
    ) => {
      setRespostas((prev) => ({
        ...prev,
        [`adimplencia-assoc-${index}-${field}`]: value,
      }));
    };

    const statusBySection = useMemo(() => {
      const map: Record<string, boolean> = {};

      const isBlank = (value?: string | null) => !String(value || "").trim();
      const hasFile = (secaoId: string) => !isBlank(arquivos[secaoId]);

      secoes.forEach((secao) => {
        if (secao.tipo === "somente-analista") {
          map[secao.id] = false;
          return;
        }

        if (secao.id === "adimplencia") {
          const formaCobranca = respostas["adimplencia-formaCobranca"] ?? "";
          const quantidade = parseInt(respostas["adimplencia-quantidade"] ?? "0", 10) || 0;
          const tipoDocumento = respostas["adimplencia-tipoDocumento"] ?? "";

          let pendente =
            isBlank(formaCobranca) || quantidade <= 0 || isBlank(tipoDocumento);

          if (!pendente) {
            for (let i = 0; i < quantidade; i++) {
              const cnpj = respostas[`adimplencia-assoc-${i}-cnpj`] ?? "";
              const nomeSocial = respostas[`adimplencia-assoc-${i}-nomeSocial`] ?? "";
              const chave = `adimplencia-assoc-${i}`;
              const arquivoIds = arquivosIdsPorSecao[chave] ?? [];
              const arquivoNome = arquivos[chave] ?? "";

              const hasAssocFile = arquivoIds.length > 0 || !isBlank(arquivoNome);

              if (isBlank(cnpj) || isBlank(nomeSocial) || !hasAssocFile) {
                pendente = true;
                break;
              }
            }
          }

          map[secao.id] = pendente;
          return;
        }

        if (secao.tipo === "pergunta") {
          map[secao.id] = isBlank(respostas[secao.id]);
          return;
        }

        if (secao.tipo === "upload") {
          map[secao.id] = !hasFile(secao.id);
          return;
        }

        if (secao.tipo === "pergunta-com-upload") {
          const resposta = respostas[secao.id] ?? "";

          if (isBlank(resposta)) {
            map[secao.id] = true;
            return;
          }

          // Normalizamos para suportar variações vindas do backend (ex.: "NAO", "Não", etc.).
          if (normalizarValorSimNaoParaUI(resposta) === "NÃO") {
            map[secao.id] = false;
            return;
          }

          map[secao.id] = !hasFile(secao.id);
          return;
        }

        map[secao.id] = true;
      });

      return map;
    }, [arquivosIdsPorSecao, arquivos, respostas, secoes]);

    useEffect(() => {
      onStatusChange?.(statusBySection);
    }, [onStatusChange, statusBySection]);

    useImperativeHandle(
      ref,
      () => ({
        handleSave,
        isDirty,
        getCamposAlterados: montarCamposAlterados,
        getAnaliseCriterioSolicitacaoAlterados: montarAnaliseCriterioSolicitacaoAlterados,
        markAsSaved,
        toggleAll,
        hasPendingUploads: () => Boolean(uploadProcessingKey),
        getNomesArquivoLocais: () => ({ ...nomeArquivoOriginalClienteRef.current }),
      }),
      [
        handleSave,
        isDirty,
        montarCamposAlterados,
        montarAnaliseCriterioSolicitacaoAlterados,
        markAsSaved,
        toggleAll,
        uploadProcessingKey,
      ],
    );

    const receitaReferenciaPadraoParaDre = useMemo(() => {
      const lista = dadosIndicadoresAnalise?.receitasOperacionais;
      if (!lista?.length) return null;
      const sorted = [...lista].sort(
        (a, b) => b.anoReferencia - a.anoReferencia,
      );
      return sorted[0]?.valor ?? null;
    }, [dadosIndicadoresAnalise]);

    const receitaReferenciaParaDre =
      typeof receitaOperacionalReferencia === "number" &&
      Number.isFinite(receitaOperacionalReferencia)
        ? receitaOperacionalReferencia
        : receitaReferenciaPadraoParaDre;
    const associadosReferenciaPadrao = useMemo(() => {
      const lista = dadosIndicadoresAnalise?.associados;
      if (!lista?.length) return null;
      const sorted = [...lista].sort((a, b) => b.anoReferencia - a.anoReferencia);
      const val = Number(sorted[0]?.quantidade);
      return Number.isFinite(val) ? val : null;
    }, [dadosIndicadoresAnalise]);
    const associadosReferenciaParaIndicadores =
      typeof associadosReferencia === "number" && Number.isFinite(associadosReferencia)
        ? associadosReferencia
        : associadosReferenciaPadrao;

    if (!isActive) return null;

    return (
      <Container className="doc-obrigatorios-container">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          <h1 className="cadastro-title" style={{ margin: 0 }}>
            {tituloAba}
          </h1>

          {secoes.length > 0 && (
            <button
              type="button"
              onClick={toggleAll}
              className="shipping-toggle-all"
              aria-label={isAllOpen ? "Recolher tudo" : "Expandir tudo"}
              title={isAllOpen ? "Recolher todos os tópicos" : "Expandir todos os tópicos"}
              style={{
                background: "transparent",
                border: "none",
                color: "#005baa",
                fontWeight: 600,
                cursor: "pointer",
                padding: 0,
                whiteSpace: "nowrap",
              }}
            >
              {isAllOpen ? "Recolher tudo" : "Expandir tudo"}
            </button>
          )}
        </div>

        {descricaoGeral && (
          <div className="doc-obrigatorios-info">
            <em dangerouslySetInnerHTML={{ __html: descricaoGeral }} />
          </div>
        )}

        {secoes.length === 0 ? (
          <p className="text-muted">
            Nenhuma seção configurada para este ano. Consulte o backend.
          </p>
        ) : (
          <>
            {secoes.map((secao) => {
              const analiseKey = getAnaliseKeyFromSecao(secao);
              const histAdequ = historicoAdequacaoPorAnaliseKey?.[analiseKey];
              const observacaoAnalistaAtual =
                analiseCampos[analiseKey]?.observacaoAnalista ?? "";
              const aguardandoRetornoAdequacao =
                analiseAguardandoRetornoPorSecaoId?.[secao.id] ?? false;
              const aguardandoRetornoPorHistorico = Boolean(
                histAdequ?.solicitadoEm && !histAdequ?.adequadoEm,
              );
              const destacarAdequacaoAguardando =
                aguardandoRetornoAdequacao || aguardandoRetornoPorHistorico;
              const bloquearAnaliseEdicao =
                destacarAdequacaoAguardando || !analisePodeEditar;
              const statusParecerAnalista =
                analiseCampos[analiseKey]?.status ?? "";
              const analistaJaFechouParecerRodadaAtual =
                statusParecerAnalista === "CONFORME" ||
                statusParecerAnalista === "NAO_CONFORME";
              const analistaComRetornoSindicato =
                Boolean(analiseCamposPorSecaoId) &&
                Boolean(histAdequ?.solicitadoEm && histAdequ.adequadoEm) &&
                !destacarAdequacaoAguardando &&
                statusParecerAnalista === "DEVOLVER";
              const observacaoAnalistaExibida = observacaoAnalistaAtual;
              const podeEditarObsSindicatoHistorico =
                !readOnly &&
                !analiseCamposPorSecaoId &&
                Boolean(histAdequ?.solicitadoEm) &&
                !histAdequ?.adequadoEm &&
                secao.canEditObservacao !== false;
              const pendenteSecao = Boolean(statusBySection[secao.id]);
              const respostaPerguntaVazia = !String(
                respostas[secao.id] ?? "",
              ).trim();
              const secaoTemArquivo =
                (arquivosIdsPorSecao[secao.id]?.length ?? 0) > 0 ||
                String(arquivos[secao.id] ?? "").trim().length > 0;
              const uploadObrigatorioNestaSecao =
                secao.tipo === "upload" ||
                (secao.tipo === "pergunta-com-upload" &&
                  normalizarValorSimNaoParaUI(respostas[secao.id] ?? "") !==
                    "NÃO");
              const ciclosHist =
                ciclosHistoricoAdequacaoPorAnaliseKey?.[analiseKey] ?? [];
              const usarHistoricoCiclosAnalista =
                Boolean(analiseCamposPorSecaoId) && ciclosHist.length > 0;

              return (
              <AccordionSection
                key={secao.id}
                id={secao.id}
                title={secao.titulo}
                linkModelo={secao.linkModelo}
                isOpen={openBySection[secao.id]}
                onToggle={() => handleToggleOne(secao.id)}
                titlePending={pendenteSecao}
                titleAdequacaoAguardando={destacarAdequacaoAguardando}
                titleAdequacaoRespondida={analistaComRetornoSindicato}
              >
                {secao.tipo === "somente-analista" &&
                secao.somenteAnalistaVariant ? (
                  <AnalistaIndicadoresSecao
                    variant={secao.somenteAnalistaVariant}
                    receitasOperacionais={
                      dadosIndicadoresAnalise?.receitasOperacionais ?? []
                    }
                    associados={dadosIndicadoresAnalise?.associados ?? []}
                    salarioMinimoAnoAnterior={
                      dadosIndicadoresAnalise?.salarioMinimoAnoAnterior ?? null
                    }
                    receitaOperacionalReferencia={receitaReferenciaParaDre}
                    associadosReferencia={associadosReferenciaParaIndicadores}
                    onReceitaOperacionalReferenciaChange={
                      bloquearAnaliseEdicao
                        ? undefined
                        : onReceitaOperacionalReferenciaChange
                    }
                    onAssociadosReferenciaChange={
                      bloquearAnaliseEdicao ? undefined : onAssociadosReferenciaChange
                    }
                  />
                ) : secao.id === "adimplencia" ? (
                  <AdimplenciaSection
                    formaCobranca={respostas["adimplencia-formaCobranca"] ?? ""}
                    onFormaCobrancaChange={(valor) =>
                      handlePerguntaChange("adimplencia-formaCobranca", valor)
                    }
                    observacaoGeral={observacoes["adimplencia"] ?? ""}
                    onObservacaoGeralChange={(valor) =>
                      handleObservacaoChange("adimplencia", valor)
                    }
                    quantidadeAssociados={quantidadeAssociados}
                    onQuantidadeAssociadosChange={(quantidade) =>
                      handlePerguntaChange("adimplencia-quantidade", String(quantidade))
                    }
                    tipoDocumento={respostas["adimplencia-tipoDocumento"] ?? ""}
                    onTipoDocumentoChange={(valor) =>
                      handlePerguntaChange("adimplencia-tipoDocumento", valor)
                    }
                    associados={associadosAdimplencia}
                    maxSizeMB={secao.maxSizeMB ?? 300}
                    onAssociadoChange={handleAdimplenciaAssociadoChange}
                    arquivosAssociados={arquivosAdimplenciaAssoc}
                    onUploadEscolher={(index) =>
                      handleUploadEscolher(`adimplencia-assoc-${index}`)
                    }
                    onUploadRemover={(index) =>
                      handleUploadRemover(`adimplencia-assoc-${index}`)
                    }
                    onUploadFileSelected={(index, file, options) =>
                      handleAdimplenciaFileSelected(index, file, options)
                    }
                    associadoPodeBaixarArquivo={(index) =>
                      (arquivosIdsPorSecao[`adimplencia-assoc-${index}`]?.length ?? 0) > 0
                    }
                    uploadDownloadLoadingKey={downloadLoadingKey}
                    uploadProcessingKey={uploadProcessingKey}
                    onUploadDownload={(index) =>
                      void handleDownloadArquivoPorSecao(
                        `adimplencia-assoc-${index}`,
                        arquivos[`adimplencia-assoc-${index}`],
                      )
                    }
                    observacoesAssociados={observacoesAdimplenciaAssoc}
                    onObservacaoAssociadoChange={(index, valor) =>
                      handleObservacaoChange(`adimplencia-assoc-${index}`, valor)
                    }
                    readOnly={
                      readOnly ||
                      (secao.canEditPergunta === false &&
                        secao.canEditUpload === false &&
                        secao.canEditObservacao === false)
                    }
                  />
                ) : (
                  <>
                    {(secao.tipo === "pergunta" ||
                      secao.tipo === "pergunta-com-upload") &&
                      secao.pergunta &&
                      secao.opcoes && (
                        <CampoPergunta
                          name={`${secao.id}-pergunta`}
                          pergunta={secao.pergunta}
                          opcoes={secao.opcoes}
                          valor={respostas[secao.id]}
                          onChange={(valor) => handlePerguntaChange(secao.id, valor)}
                          disabled={readOnly || secao.canEditPergunta === false}
                          highlightRequired={
                            pendenteSecao &&
                            respostaPerguntaVazia
                          }
                        />
                      )}

                    {secao.id !== "adimplencia" &&
                      (secao.tipo === "upload" ||
                        (secao.tipo === "pergunta-com-upload" &&
                          normalizarValorSimNaoParaUI(respostas[secao.id] ?? "") !==
                            "NÃO")) && (
                        <CampoUpload
                          instrucao={secao.instrucaoUpload ?? ""}
                          valorArquivo={arquivos[secao.id] ?? null}
                          onEscolher={() => handleUploadEscolher(secao.id)}
                          onRemover={() => void handleUploadRemover(secao.id)}
                          onFileSelected={(file, options) =>
                            void handleFileSelected(secao.id, file, options)
                          }
                          onDownload={
                            (arquivosIdsPorSecao[secao.id]?.length ?? 0) > 0
                              ? () =>
                                  void handleDownloadArquivoPorSecao(
                                    secao.id,
                                    arquivos[secao.id],
                                  )
                              : undefined
                          }
                          downloadLoading={downloadLoadingKey === secao.id}
                          uploadPending={uploadProcessingKey === secao.id}
                          highlightRequiredEmpty={
                            pendenteSecao &&
                            uploadProcessingKey !== secao.id &&
                            uploadObrigatorioNestaSecao &&
                            !secaoTemArquivo
                          }
                          maxSizeMB={secao.maxSizeMB ?? 300}
                          accept={secao.accept ?? ".pdf"}
                          disabled={readOnly || secao.canEditUpload === false}
                          disabledObservacao={
                            readOnly || secao.canEditObservacao === false
                          }
                          observacao={observacoes[secao.id]}
                          onObservacaoChange={
                            typeof secao.campoObservacaoId === "number"
                              ? (valor) => handleObservacaoChange(secao.id, valor)
                              : undefined
                          }
                        />
                      )}

                    {typeof receitaReferenciaParaDre === "number" &&
                      tituloPareceCriterioDre(secao.titulo) &&
                      secao.tipo !== "somente-analista" && (
                        <Form.Group className="mt-3">
                          <p className="fw-bold mb-2">Receita Operacional*</p>
                          <p
                            className="text-muted small mb-3"
                            style={{ textAlign: "justify" }}
                          >
                            Receita Operacional: aquela oriunda de ações que
                            efetivamente houve empenho da entidade. Como as
                            Contribuições, prestação de serviços e demais que se
                            enquadrem nessa classificação. Receitas com Aplicações
                            financeiras e subsídio oferecido pela FIEMG não são
                            receitas de caráter operacional para fins de cálculo.
                          </p>
                          <Form.Label className="fw-bold">
                            Receita operacional (referência)
                          </Form.Label>
                          <Form.Control
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            readOnly={!analiseCamposPorSecaoId}
                            className={!analiseCamposPorSecaoId ? "bg-light" : undefined}
                            placeholder="Ex.: 60.000,00"
                            value={formatarMoedaBrSemSimbolo(
                              typeof receitaReferenciaParaDre === "number" &&
                                Number.isFinite(receitaReferenciaParaDre)
                                ? receitaReferenciaParaDre
                                : 0,
                            )}
                            onChange={(e) => {
                              if (!analiseCamposPorSecaoId) return;
                              const digitos = e.target.value.replace(/\D/g, "");
                              const numero = digitos ? Number(digitos) / 100 : 0;
                              onReceitaOperacionalReferenciaChange?.(numero);
                            }}
                          />
                        </Form.Group>
                      )}
                  </>
                )}
                {analiseCamposPorSecaoId ? (
                  <div
                    className={[
                      "border-top pt-3 mt-3",
                      destacarAdequacaoAguardando
                        ? "rounded border border-warning overflow-hidden"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {destacarAdequacaoAguardando ? (
                      <div
                        className="bg-warning text-dark fw-semibold text-center py-2 px-3 small border-bottom border-warning"
                        role="status"
                      >
                        Solicitação de adequação aguardando o sindicato atuar neste
                        critério.
                      </div>
                    ) : null}
                    <div
                      className={
                        destacarAdequacaoAguardando
                          ? "p-3 bg-warning-subtle"
                          : undefined
                      }
                    >
                    {bloquearAnaliseEdicao ? (
                      <Alert variant="warning" className="py-2 small mb-3">
                        {analiseBloqueadaPorAdequacaoGlobal
                          ? "Edição bloqueada: há solicitação de adequação aguardando retorno do sindicato. A análise não está concluída — continue após o reenvio."
                          : destacarAdequacaoAguardando
                            ? "Edição bloqueada: existe adequação pendente para este critério."
                            : "Edição bloqueada: a análise já foi concluída"}
                      </Alert>
                    ) : null}
                    {usarHistoricoCiclosAnalista ? (
                      <AdequacaoHistoricoCiclos
                        secaoId={secao.id}
                        ciclos={ciclosHist}
                        open={historicoAdequacaoAbertoPorSecaoId[secao.id] ?? false}
                        onToggle={() =>
                          setHistoricoAdequacaoAbertoPorSecaoId((p) => ({
                            ...p,
                            [secao.id]: !p[secao.id],
                          }))
                        }
                      />
                    ) : histAdequ?.solicitadoEm ? (
                      <div
                        className="shipping-analise-historico-card p-3 rounded border mb-4"
                        data-testid={`historico-adequacao-${secao.id}`}
                      >
                        <h6 className="fw-bold mb-1">SOLICITADO ADEQUAÇÃO</h6>
                        <p className="small text-muted mb-3">
                          Pedido registrado e resposta do sindicato (somente leitura).
                        </p>
                        <Form.Group className="mb-2">
                          <Form.Label className="small text-muted mb-0">
                            Solicitado em
                          </Form.Label>
                          <Form.Control
                            type="text"
                            disabled
                            className="bg-light"
                            value={formatDateTimeBR(histAdequ.solicitadoEm)}
                          />
                        </Form.Group>
                        {histAdequ.adequadoEm ? (
                          <Form.Group className="mb-2">
                            <Form.Label className="small text-muted mb-0">
                              Adequado em
                            </Form.Label>
                            <Form.Control
                              type="text"
                              disabled
                              className="bg-light"
                              value={formatDateTimeBR(histAdequ.adequadoEm)}
                            />
                          </Form.Group>
                        ) : null}
                        <Form.Group className="mb-2">
                          <Form.Label className="small text-muted mb-0">
                            Observação analista (no pedido)
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            disabled
                            className="bg-light"
                            value={histAdequ.observacaoAnalista ?? ""}
                          />
                        </Form.Group>
                        <Form.Group className="mb-0">
                          <Form.Label className="small text-muted mb-0">
                            Observação sindicato
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            disabled
                            className="bg-light"
                            value={histAdequ.observacaoSindicato ?? ""}
                          />
                        </Form.Group>
                      </div>
                    ) : null}

                    <div
                      className={
                        histAdequ?.solicitadoEm
                          ? "shipping-analise-nova-card p-3 rounded border"
                          : undefined
                      }
                    >
                      <h6
                        className={
                          destacarAdequacaoAguardando
                            ? "fw-bold mb-1 text-decoration-underline"
                            : "fw-bold mb-1"
                        }
                      >
                        ANÁLISE
                      </h6>
                      <p className="small text-muted mb-3">
                        Situação atual do critério e observações desta rodada de
                        análise.
                      </p>
                      <div className="d-flex flex-column gap-2 mb-3">
                      <Form.Check
                        type="radio"
                        name={`analise-status-${secao.id}`}
                        label="PENDENTE"
                        checked={(analiseCampos[analiseKey]?.status ?? "") === ""}
                        onChange={() => handleAnaliseStatusChange(secao.id, "")}
                        disabled={bloquearAnaliseEdicao}
                      />
                      <Form.Check
                        type="radio"
                        name={`analise-status-${secao.id}`}
                        label="CONFORME"
                        checked={analiseCampos[analiseKey]?.status === "CONFORME"}
                        onChange={() =>
                          handleAnaliseStatusChange(secao.id, "CONFORME")
                        }
                        disabled={bloquearAnaliseEdicao}
                      />
                      <Form.Check
                        type="radio"
                        name={`analise-status-${secao.id}`}
                        label="NÃO CONFORME"
                        checked={analiseCampos[analiseKey]?.status === "NAO_CONFORME"}
                        onChange={() =>
                          handleAnaliseStatusChange(secao.id, "NAO_CONFORME")
                        }
                        disabled={bloquearAnaliseEdicao}
                      />
                      {!secao.somenteAnalistaVariant ? (
                        <Form.Check
                          type="radio"
                          name={`analise-status-${secao.id}`}
                          label="DEVOLVER"
                          checked={analiseCampos[analiseKey]?.status === "DEVOLVER"}
                          onChange={() =>
                            handleAnaliseStatusChange(secao.id, "DEVOLVER")
                          }
                          disabled={bloquearAnaliseEdicao}
                        />
                      ) : null}
                    </div>

                    <Form.Group className="mt-2">
                      <Form.Label className="fw-bold mb-1">Observação:</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        maxLength={10000}
                        value={observacaoAnalistaExibida}
                        onChange={(e) =>
                          handleAnaliseObsAnalistaChange(secao.id, e.target.value)
                        }
                        aria-describedby={`obs-analista-hint-${secao.id}`}
                        disabled={bloquearAnaliseEdicao}
                      />
                      <Form.Text
                        id={`obs-analista-hint-${secao.id}`}
                        muted
                        className="d-flex justify-content-between"
                      >
                        <span>
                          {bloquearAnaliseEdicao
                            ? "Observação travada: análise concluída/somente leitura."
                            : "Limite máximo: 10.000 caracteres"}
                        </span>
                        <span aria-live="polite">
                          {observacaoAnalistaExibida.length}
                          /10.000
                        </span>
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mt-3">
                      <Form.Label className="fw-bold mb-1">
                        Observação interna:
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        maxLength={10000}
                        value={analiseCampos[analiseKey]?.observacaoInterna ?? ""}
                        onChange={(e) =>
                          handleAnaliseObsInternaChange(secao.id, e.target.value)
                        }
                        aria-describedby={`obs-interna-hint-${secao.id}`}
                        disabled={bloquearAnaliseEdicao}
                      />
                      <Form.Text
                        id={`obs-interna-hint-${secao.id}`}
                        muted
                        className="d-flex justify-content-between"
                      >
                        <span>
                          {bloquearAnaliseEdicao
                            ? "Observação interna travada: análise concluída/somente leitura."
                            : "Limite máximo: 10.000 caracteres"}
                        </span>
                        <span aria-live="polite">
                          {(analiseCampos[analiseKey]?.observacaoInterna ?? "").length}
                          /10.000
                        </span>
                      </Form.Text>
                    </Form.Group>
                    </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {histAdequ?.solicitadoEm &&
                    !parecerPorSecaoId?.[analiseKey]?.length ? (
                      <div className="border-top pt-3 mt-3">
                        {!histAdequ.adequadoEm ? (
                          <Alert variant="danger" className="py-2 small mb-2">
                            Realizar adequação.
                          </Alert>
                        ) : (
                          <Alert variant="success" className="py-2 small mb-2">
                            Adequação conforme solicitado pelo analista.
                          </Alert>
                        )}
                        <div className="p-3 rounded border bg-warning-subtle border-warning">
                          <h6 className="fw-bold mb-3">SOLICITADO ADEQUAÇÃO</h6>
                          <Form.Group className="mb-2">
                            <Form.Label className="small text-muted mb-0">
                              Solicitado em  
                            </Form.Label>
                            <Form.Control
                              type="text"
                              disabled
                              className="bg-light"
                              value={formatDateTimeBR(histAdequ.solicitadoEm)}
                            />
                          </Form.Group>
                          {histAdequ.adequadoEm ? (
                            <Form.Group className="mb-2">
                              <Form.Label className="small text-muted mb-0">
                                Adequado em
                              </Form.Label>
                              <Form.Control
                                type="text"
                                disabled
                                className="bg-light"
                                value={formatDateTimeBR(histAdequ.adequadoEm)}
                              />
                            </Form.Group>
                          ) : null}
                          <Form.Group className="mb-2">
                            <Form.Label className="small text-muted mb-0">
                              Observação analista
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              disabled
                              className="bg-light"
                              value={histAdequ.observacaoAnalista ?? ""}
                            />
                          </Form.Group>
                          <Form.Group className="mb-0">
                            <Form.Label className="small text-muted mb-0">
                              Observação sindicato
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              maxLength={10000}
                              disabled={!podeEditarObsSindicatoHistorico}
                              className={podeEditarObsSindicatoHistorico ? "" : "bg-light"}
                              value={
                                obsSindicatoByAnaliseKey[analiseKey] ??
                                String(histAdequ.observacaoSindicato ?? "")
                              }
                              onChange={(e) =>
                                setObsSindicatoByAnaliseKey((prev) => ({
                                  ...prev,
                                  [analiseKey]: e.target.value,
                                }))
                              }
                            />
                            {podeEditarObsSindicatoHistorico ? (
                              <Form.Text
                                muted
                                className="d-flex justify-content-between"
                              >
                                <span>Limite máximo: 10.000 caracteres</span>
                                <span aria-live="polite">
                                  {
                                    (
                                      obsSindicatoByAnaliseKey[analiseKey] ??
                                      String(histAdequ.observacaoSindicato ?? "")
                                    ).length
                                  }
                                  /10.000
                                </span>
                              </Form.Text>
                            ) : null}
                          </Form.Group>
                        </div>
                      </div>
                    ) : null}
                    {parecerPorSecaoId?.[analiseKey]?.length ? (
                      <div className="border-top pt-3 mt-3">
                        {parecerPorSecaoId[analiseKey].map((node, idx) => (
                          <React.Fragment key={`parecer-${secao.id}-${idx}`}>
                            {node}
                          </React.Fragment>
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </AccordionSection>
              );
            })}

            {!readOnly ? (
              <div className="buttons-container">
                <SaveSubmitButtons
                  onSave={onSaveAll ?? handleSave}
                  onSubmit={handleSubmit}
                  submitLabel={submitLabel}
                  submitDisabled={submitDisabled}
                />
              </div>
            ) : null}
          </>
        )}

        <NotificationToast
          show={acaoToast.show}
          onClose={() => setAcaoToast((t) => ({ ...t, show: false }))}
          message={acaoToast.message}
          type={acaoToast.type}
        />
      </Container>
    );
  },
);

DocumentosTabV2.displayName = "DocumentosTabV2";

export default DocumentosTabV2;